import {ActivatedRoute, Event, NavigationEnd, Params, Router} from '@angular/router';
import {Component, OnDestroy, OnInit} from '@angular/core';
import {firstValueFrom, Subscription} from 'rxjs';

import {BDCPConstsService} from '../../../shared/services/consts.service';
import {BDCPFilterService} from '../../../filter/services/filter.service';
import {BDCPProduct} from '../../../../models/product.model';
import {IBDCPProductsService} from '../../../shared/services/products-service/products.interface';
import {Location} from '@angular/common';
import {first} from 'rxjs/operators';

@Component({
    selector: 'bdcp-product-finder-results',
    templateUrl: './results.page.html',
    styleUrls: ['./results.page.scss']
})
export class BDCPProductFinderResultsComponent implements OnInit, OnDestroy {

    public products: BDCPProduct[] = [];
    public isBrotherResult = true;
    public alternativeMode: boolean;
    public alternativeBaseProduct: BDCPProduct;
    public pseudoProducts: any[];

    public order = 'asc';

    public filters: string[];

    private navigationSubscription: Subscription;
    private initializing: boolean;

    public constructor(
        public consts: BDCPConstsService,
        public filterService: BDCPFilterService,
        private route: ActivatedRoute,
        private router: Router,
        private productsService: IBDCPProductsService,
        private location: Location
    ) {
    }

    public async ngOnInit(): Promise<void> {
        this.navigationSubscription = this.router.events.subscribe(async (e: Event) => {
            if (e instanceof NavigationEnd) {
                await this.init();
            }
        });
        await this.init();
    }

    public ngOnDestroy(): void {
        if (this.navigationSubscription) {
            this.navigationSubscription.unsubscribe();
        }
    }

    public async navigateToCategorySelection() {
        await this.router.navigate([
            '/products',
            'product-finder'
        ]);
    }

    public orderChanged(): void {
        if (!this.order) {
            return;
        }
        this.products = this.order === 'asc'
            ? this.products.sort((a, b) => a.productModel.localeCompare(b.productModel))
            : this.products.sort((a, b) => b.productModel.localeCompare(a.productModel));
    }

    public async initFilteredProducts(query: Params): Promise<void> {
        const productCategory = query['productCategory'];

        if (!productCategory) {
            return;
        }

        const queryParams = this.getQueryParams(productCategory, query);
        this.filters = this.getFilters(productCategory, queryParams);

        const result = await this.productsService.getByFilter(this.getQueryParams(productCategory, query));

        this.isBrotherResult = result.type === 'BROTHER';
        this.products = result.products;

        this.calcPseudoProducts();
    }

    private async init(): Promise<void> {
        if (this.initializing) {
            return;
        }
        this.initializing = true;
        try {
            switch (this.route.routeConfig.path) {
                case 'results':
                    await this.initFilteredProducts(await firstValueFrom(this.route.queryParams.pipe(first())));
                    break;
                case 'search/:searchValue':
                    await this.initSearchProducts((await firstValueFrom(this.route.params.pipe(first())))['searchValue']);
                    break;
                case 'alternatives/:puid':
                    await this.initAlternativeProducts((await firstValueFrom(this.route.params.pipe(first())))['puid']);
                    break;
                case 'category/:category':
                    await this.initCategoryProducts((await firstValueFrom(this.route.params.pipe(first())))['category']);
                    break;
                default:
                    await this.initFilteredProducts(await firstValueFrom(this.route.queryParams.pipe(first())));
                    break;
            }
        } finally {
            this.initializing = false;
        }
    }

    private getQueryParams(productCategory: string, query: Params): { [key: string]: string | string[] } {
        const queryParams = {
            productCategory
        };

        let possibleQueryParams: string[] = [];

        switch (productCategory) {
            case 'labeler':
                possibleQueryParams = Object.keys(this.consts.possibleLabelerQueryParams);
                break;
            case 'mfc':
                possibleQueryParams = Object.keys(this.consts.possibleMfcQueryParams);
                break;
            case 'printer':
                possibleQueryParams = Object.keys(this.consts.possiblePrinterQueryParams);
                break;
            case 'scanner':
                possibleQueryParams = Object.keys(this.consts.possibleScannerQueryParams);
                break;
        }

        for (const key of Object.keys(query)) {
            if (possibleQueryParams.indexOf(key) > -1) {
                queryParams[key] = query[key];
            }
        }

        return queryParams;
    }

    public async initAlternativeProducts(puid: string): Promise<void> {
        this.alternativeMode = true;
        const result = await this.productsService.getComparableBrotherProducts(puid);

        this.isBrotherResult = result.type === 'BROTHER';
        this.products = result.brotherProducts;

        this.alternativeBaseProduct = result.nonBrotherProduct;

        this.calcPseudoProducts();
        this.orderChanged();
    }

    public async initSearchProducts(searchValue: string): Promise<void> {
        this.filterService.searchValue = searchValue;

        const result = await this.productsService.getBySearchValue(searchValue);

        this.isBrotherResult = result.type === 'BROTHER';
        this.products = result.products;

        this.calcPseudoProducts();
        this.orderChanged();
    }

    public async initCategoryProducts(category: string): Promise<void> {
        const result = await this.productsService.getByCategory(category);

        this.filters = [this.consts.productCategoryMap[category]];

        this.isBrotherResult = result.type === 'BROTHER';
        this.products = result.products;

        this.calcPseudoProducts();
        this.orderChanged();
    }

    public back(): void {
        this.location.back();
    }

    private calcPseudoProducts(): void {
        this.pseudoProducts = Array(!this.products
            ? 0
            : 3 - (this.products.length % 3)).fill(0);
    }


    private getFilters(productCategory: string, queryParams: { [key: string]: string | string[] }): string[] {
        let possibleQueryParams: string[] = [];
        let map = {};

        const filters = [this.consts.productCategoryMap[productCategory]];

        switch (productCategory) {
            case 'labeler':
                map = this.consts.labelerToDisplay;
                possibleQueryParams = Object.keys(this.consts.possibleLabelerQueryParams);
                break;
            case 'mfc':
                map = this.consts.mfcToDisplay;
                possibleQueryParams = Object.keys(this.consts.possibleMfcQueryParams);
                break;
            case 'printer':
                map = this.consts.printerToDisplay;
                possibleQueryParams = Object.keys(this.consts.possiblePrinterQueryParams);
                break;
            case 'scanner':
                map = this.consts.scannerToDisplay;
                possibleQueryParams = Object.keys(this.consts.possibleScannerQueryParams);
                break;
            case 'fax':
                map = this.consts.faxToDisplay;
                possibleQueryParams = Object.keys(this.consts.possibleFaxQueryParams);
                break;
        }

        for (const key of Object.keys(queryParams)) {
            if (key === 'productCategory' || possibleQueryParams.indexOf(key) === -1) {
                continue;
            }
            const fn = map[key];
            if (!fn) {
                continue;
            }
            const v = queryParams[key];
            if (Array.isArray(v)) {
                for (const e of v) {
                    filters.push(fn(e));
                }
            } else {
                filters.push(fn(v));
            }
        }

        return filters;
    }

    onImageError(event: any): void {
        const target = event.target as HTMLImageElement | null;
        if (target) {
            target.src = 'assets/images/product_img_dummy.png';
        }
    }
}
